/* app/coverage-map.jsx - the Coverage Map: which areas of this site's taxonomy
   have thin or no content, and which pages this website does not have at all.

   TWO QUESTIONS, ONE SCREEN. The map is about the BANKS: which categories and
   tags have content behind them. The section under it (PageGaps, further down)
   is about the WEBSITE: which page this business should have and does not. They
   share a screen because both are coverage and an owner should not have to
   visit two places to read one answer, but they are separate reads with
   separate failures, and neither renders the other's empty state.

   Reads GET /api/coverage/taxonomy. The axis is the site's own vocabulary (its
   live WordPress categories and tags, plus the suggestions it has approved) and
   the measure is the page inventory the last coverage scan stored, so this is a
   database read rather than a crawl. POST /api/coverage/sync is the scan that
   fills that inventory, and it is the same scan the Keywords Planner runs.

   THE HONESTY IS THE FEATURE, and it is the only reason this screen is worth
   more than a spreadsheet. A term nothing is filed under and a term nobody has
   scanned produce the same zero, and the second one rendered as the first says
   "you have covered none of this" about a site nothing has ever read. So a
   count is a number OR it is null, never both, and every null says which kind
   of absence it is: nothing scanned this site, the scan stopped at its ceiling
   before it could have reached those pages, or the term name is too short to
   look for without matching by accident.

   What a count means is stated on the screen too, not just in the API. A page
   counts under a term when the term's name appears in its title or slug, which
   is a fact about naming and not about which terms WordPress filed the post
   under. Left unsaid, a category with 40 posts and an unrelated name would read
   as an empty section of the site. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useEffect, useCallback, useRef } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  /* A finding as one line, its explanation in the drawer the line opens (see
     app/report-drawer.jsx). Resolved at RENDER time rather than at import, the
     same way research-keywords.jsx reaches it, so this module does not depend
     on script order; if the module were ever missing, the children render
     inline instead of the line vanishing with them. */
  const DetailLine = (p) => {
    const C = window.MlReportDrawer && window.MlReportDrawer.DetailLine;
    return C ? React.createElement(C, p) : React.createElement("div", { className: "ds-card" }, p.children);
  };

  // A map has an age. One drawn over a scan from three weeks ago is not the
  // current state of the site and has to say so.
  function ago(ts) {
    if (!ts) return "";
    const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
  }

  /* The four states a term can be in, and they are four rather than three on
     purpose. "Not known" is not a shade of "nothing found": one is a finding a
     user can act on, the other is an admission that nothing looked. Merging
     them is the single thing that would make this screen lie. */
  const BANDS = {
    none:    { label: "Nothing found", tone: "danger" },
    thin:    { label: "Thin",          tone: "warning" },
    covered: { label: "Covered",       tone: "success" },
    unknown: { label: "Not known",     tone: "neutral" },
  };
  const BAND_ORDER = ["none", "thin", "covered", "unknown"];

  const SOURCE_LABEL = {
    wordpress: "In WordPress",
    "approved suggestion": "Approved suggestion",
  };

  function bandBadge(band) {
    const { Badge } = window.HuddleDesignSystem_775113;
    const b = BANDS[band] || BANDS.unknown;
    return <Badge tone={b.tone} dot={band !== "unknown"}>{b.label}</Badge>;
  }

  // One published page. A page with a URL opens; one without is still shown,
  // because dropping it would understate the count the row is claiming.
  function PageChip({ page }) {
    const label = page.title || page.url || `post #${page.post_id}`;
    return page.url
      ? <a className="ds-chip" href={page.url} target="_blank" rel="noopener noreferrer" title={`Open ${page.url}`}>{label}</a>
      : <span className="ds-chip" title="The scan stored no URL for this page, so it cannot be opened from here.">{label}</span>;
  }

  // The scan's own milestones, from GET /api/runs/:id. Nothing here is on a
  // timer: when the server stops reaching milestones the bar stops moving,
  // which is the point of reading progress rather than inventing it.
  function ScanProgress({ run }) {
    const failed = !!run && run.status === "failed";
    return (
      <div className="bi-fetch-progress">
        <div className="ml-progress-row" style={{ marginBottom: 10 }}>
          <div className="ml-progress"><div className="ml-progress__bar" style={{ width: (run ? run.pct : 0) + "%" }} /></div>
          <span className="ml-progress-row__pct">{run ? run.pct : 0}%</span>
        </div>
        <div className="bi-fetch-step bi-fetch-step--active">
          <span className="bi-fetch-step__dot">
            {failed ? <I name="alert-triangle" size={14} /> : <span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />}
          </span>
          <span className="bi-fetch-step__label">
            {run ? `Step ${Math.max(1, run.step)} of ${run.steps} · ${run.label || "working"}` : "Starting the scan"}
            {run && run.note ? <span style={{ color: "var(--text-muted)" }}> · {run.note}</span> : null}
          </span>
        </div>
      </div>
    );
  }

  /* The count cell. Three different things live in this column and each one
     gets its own rendering, because a dash, a zero and a "3 or more" are
     answers to different questions:
       null   nothing counted this term, and `why` says what stopped it
       0      counted, and the site publishes nothing under it
       n      counted, and a capped scan can only ever prove a floor */
  function countCell(t) {
    if (t.pages == null) {
      return <span style={{ color: "var(--text-muted)" }} title={t.why || "Not counted."}>not counted</span>;
    }
    return (
      <span title={t.at_least ? "The last scan stopped at its ceiling, so this is the number it could prove, not necessarily the number there is." : undefined}>
        {t.at_least ? `${t.pages} or more` : t.pages}
      </span>
    );
  }

  /* ==========================================================================
     PAGES THIS WEBSITE DOES NOT HAVE (GET /api/coverage/page-gaps)

     Everything above answers "which of my categories and tags have content
     behind them", which is a question about the banks. This answers the
     question an owner actually arrives with: which page should this business
     have and does not. The two belong on one screen because both are coverage,
     and splitting them would send the owner to two places to read one answer,
     but this is its own component with its own read, so a failure in one never
     renders as an empty result in the other.

     THE ONE THING THIS MUST NOT DO is render an empty gap list as "nothing is
     missing". lib/page-gaps.js refuses to compute gaps at all when the page
     universe could not be read, because a gap list measured against no pages is
     a list of every cluster and every offering in the register, which would
     tell the owner to rebuild a website they already have. That refusal arrives
     as universe.known false WITH an empty gaps array, and those two together
     mean the opposite of a clean bill of health. So there are three states and
     a list of gaps is only one of them:

       universe.known false      nothing is reported, and the screen says why
       universe.complete false   the gaps show, as a floor, with why
       known, complete, empty    genuinely nothing missing, named against the
                                 counts it checked, because "no gaps" with no
                                 evidence behind it reads like a feature that
                                 did not run

     NOTHING IS RE-DERIVED HERE. The scope being the property rather than the
     site, which site a gap belongs to, whether anything can be written there,
     and the sentence explaining each of those are all decided by the endpoint
     and rendered verbatim. A second opinion in the client is how one screen
     comes to disagree with another about what a website is.

     The shape is the house one: a finding is ONE LINE carrying what the page
     would be, with its action on the line and everything that explains it in
     the drawer the line opens (app/report-drawer.jsx DetailLine). The prose
     that would otherwise sit above the work, the caveats and the account of
     what was measured, are lines too.
     ========================================================================== */

  // Grouped by kind, and a kind with nothing in it STAYS, carrying its zero and
  // the count it checked. A source that vanishes when it finds nothing is a
  // source the reader cannot tell apart from one that never ran.
  const GAP_KIND_ORDER = ["cluster", "offering", "market"];
  const GAP_KINDS = {
    cluster:  { label: "Keyword clusters", icon: "search", tone: "opportunity" },
    offering: { label: "Products and services", icon: "box", tone: "todo" },
    market:   { label: "Languages", icon: "globe", tone: "todo" },
  };

  /* The page type as the response states it, never widened. 'other' is the
     market source saying it knows a page is missing and does not know what kind
     of page it is, so it is rendered as the admission it is rather than being
     dressed up as a landing page. */
  const PAGE_TYPE = { landing: "Landing page", service: "Service page", other: "Page, kind unknown" };

  /* What the line says the page would be, per kind, with the evidence that
     makes it worth reading at a glance: a cluster's demand, the register for an
     offering, the existing counterpart for a language gap. */
  function gapSentence(g) {
    if (g.kind === "cluster") {
      const kw = plural(g.keyword_count, "keyword", "keywords");
      return g.volume
        ? `A landing page for "${g.title}", ${g.volume} searches a month across ${kw}`
        : `A landing page for "${g.title}", ${kw} with no volume recorded`;
    }
    if (g.kind === "offering") return `A service page for "${g.title}", which the register says you provide`;
    return `A page at ${g.suggested_path || g.missing_prefix || "/"}, the ${g.missing_prefix} counterpart of "${g.title}"`;
  }

  /* One evidence item, named. A count the user cannot see the names behind is
     the kind of number lib/product-gaps.js refuses to produce, and this is the
     rendering of that rule: every gap shows its items, and a keyword with no
     known volume says so instead of showing a zero. */
  function evidenceChip(e, i) {
    if (e.type === "keyword") {
      return (
        <span className="ds-chip" key={i}
              title={e.volume == null ? "No search volume is recorded for this keyword." : `${e.volume} searches a month`}>
          <b>{e.label}</b>{e.volume == null ? " (volume unknown)" : ` (${e.volume} a month)`}
        </span>
      );
    }
    if (e.type === "offering") {
      return <span className="ds-chip" key={i}><b>{e.label}</b>{e.category ? ` (${e.category})` : ""}{e.market ? ` (${e.market})` : ""}</span>;
    }
    if (e.type === "page") {
      const label = `${e.label}${e.path ? ` at ${e.path}` : ""}`;
      return e.url
        ? <a className="ds-chip" key={i} href={e.url} target="_blank" rel="noopener noreferrer" title={`Open ${e.url}`}>{label}</a>
        : <span className="ds-chip" key={i}>{label}</span>;
    }
    return <span className="ds-chip" key={i}>{e.label || "unnamed"}</span>;
  }

  const evidenceText = (e) => {
    if (e.type === "keyword") return `${e.label}${e.volume == null ? " (volume unknown)" : ` (${e.volume} searches a month)`}`;
    if (e.type === "offering") return `${e.label}${e.category ? `, ${e.category}` : ""}${e.market ? `, ${e.market}` : ""} (in the Products register)`;
    if (e.type === "page") return `${e.label}${e.path ? ` at ${e.path}` : ""}${e.url ? ` (${e.url})` : ""}`;
    return e.label || "unnamed";
  };

  /* What can be done about this gap, in the words the deliverable allows.
     `can_create` is per SITE and not per property, which is why it is read off
     the gap: on one website the blog can be written into while the marketing
     site has no CMS at all, so the same shape of finding is a page on one piece
     and a brief for a developer on the other. Offering "create the page" on the
     second would be offering something that cannot happen. */
  function deliverableText(g, planCan, sectionName) {
    const site = g.site_name || "the site this belongs to";
    if (g.deliverable === "create_page") {
      return `${site} has a CMS connected, so this page can be created in it. `
        + (planCan
          ? `Plan it makes this finding a piece of a plan on ${sectionName}, with its evidence attached, so nobody has `
            + `to retype it; writing the page itself is still a separate job. Copy brief puts this whole brief on your `
            + `clipboard instead.`
          // The second section, which is the half people are surprised by: the
          // page could be published where its evidence lives, and a plan on
          // THIS section still has no CMS to publish through, so what a piece
          // planned from here delivers is a brief. The capture takes exactly
          // the same position, and says so in its own words on the piece.
          : `${sectionName} is the section this screen is reading, so it is the one a plan here would be written onto, `
            + `and it is read by crawling it with no CMS behind it. So a piece planned from this screen delivers a `
            + `brief for whoever builds the site rather than a page to publish. Copy brief puts this whole brief on `
            + `your clipboard.`);
    }
    return `${site} is read by crawling it and has no CMS behind it, so there is nothing here to create a page in and `
      + `no button can change that. The deliverable is a brief for whoever builds the site: Copy the handover brief `
      + `puts it on your clipboard, and Plan the handover brief makes it a piece of a plan whose deliverable is that `
      + `brief rather than a published page.`;
  }

  /* The brief, as text. Everything the drawer shows, in the order a person
     writing the page needs it, so what is pasted into a plan or sent to a
     developer is the finding itself and not a retyping of it. */
  function briefOf(g, measure) {
    const lines = [
      `Page to create: ${g.title}`,
      `Kind of page: ${PAGE_TYPE[g.page_type] || g.page_type}`,
      g.suggested_path ? `Suggested path: ${g.suggested_path}` : `Suggested slug: ${g.slug}`,
      `Site: ${g.site_name || "unknown"}${g.can_create ? "" : " (no CMS: this page has to be built by whoever builds the site)"}`,
      "",
      `Why: ${g.why}`,
      `Which site: ${g.why_site}`,
      "",
      "Evidence:",
      ...(g.evidence || []).map(e => `- ${evidenceText(e)}`),
    ];
    if (!g.certain) {
      lines.push("", "Candidate, not settled: the page list this was measured against is incomplete, so this page may "
        + "exist on a part of the website nothing has read yet.");
    }
    lines.push("", `How a gap is decided: ${measure}`);
    return lines.join("\n");
  }

  /* ==========================================================================
     PLANNING A GAP, which is POST /api/content-plans/:planId/items/from-gap

     The gap lines carried a "Plan it" that opened the Content Planner and left
     the person to retype the finding, because for a while there was no endpoint
     to capture it. There is one now, so the button does the thing its name says
     and the help text under it promised.

     THE ENDPOINT NEEDS A PLAN AND A GAP DOES NOT KNOW ONE. That is the whole
     design question here, and the answer is that the customer says which, in a
     chooser, every time. Not the most recently touched plan: a plan is work
     somebody built with a purpose, and dropping a marketing page into whichever
     plan was open last is exactly the kind of thing nobody would have asked
     for and everybody would have to undo. Not a plan invented per gap either,
     which would turn 67 findings into 67 plans. So the plans on this section
     are listed with what each already holds, and a new one can be started here
     by naming it, which is still a choice made out loud and never a side
     effect of pressing Plan it.

     WHAT THE SCREEN MAY NOT DO, and each of these is a rule this codebase
     already paid for: it does not decide whether a gap exists, it does not
     create a piece by any other route, and it does not count anything itself.
     Every figure and every sentence below is the endpoint's own.
     ========================================================================== */

  // The name a brand new plan is offered with. A bucket rather than one page,
  // because the next gap planned should find it in the list instead of making
  // a second plan beside it. The customer can type over it.
  const NEW_PLAN_TITLE = "Pages this website does not have";

  // The production statuses, in words rather than in column values. A piece
  // sitting at in_review must not read as "in_review" on a screen.
  const STATUS_WORD = {
    idea: "an idea", briefed: "briefed", drafting: "being drafted", in_review: "in review",
    scheduled: "scheduled", published: "published", archived: "archived",
  };

  /* What the endpoint answered, as something a person can act on.
     Four answers and they are four rather than one, because they mean entirely
     different things and this screen is where somebody does something about it:

       the page list could not be read   the whole list is untrustworthy, so no
                                         gap on it can be planned at all
       the gap is not reported any more  the website changed under the list
       the plan is gone                  a plan was deleted while this was open
       anything else                     said as itself, with nothing planned

     The headline is in customer language. The endpoint's own sentence rides
     underneath it as a note, word for word, the way every other sentence on
     this screen does: it names the figures the refusal rests on, and rewriting
     it here would be a second version of the claim. */
  function refusalFor(e, sectionName) {
    const d = (e && e.data) || {};
    if (d.code === "UNIVERSE_UNKNOWN") {
      return {
        title: "There is no page list for this website yet, so nothing on this list can be planned",
        lines: [
          ...((d.notes || []).length ? d.notes : [(d.universe && d.universe.why) || e.message]),
          d.why_refused || "",
        ].filter(Boolean),
        stale: true,
      };
    }
    if (d.code === "NO_SUCH_GAP") {
      return {
        title: "This page is no longer one of the pages this website is missing",
        lines: [
          "The answer was worked out again just now and this page was not in it. Usually that means the website has "
          + "changed since this list was drawn: the page may exist now, or the keywords or products behind it may have "
          + "moved. Nothing was planned.",
        ],
        note: e.message,
        stale: true,
      };
    }
    if (e && e.status === 404) {
      return {
        title: `That plan is no longer on ${sectionName}`,
        lines: ["It may have been deleted while this was open. Nothing was planned: pick another plan, or read the "
          + "list of plans again."],
        note: e.message,
        reloadPlans: true,
      };
    }
    return {
      title: "Nothing was planned",
      lines: [e && e.message ? e.message : "The piece could not be created, and nothing was changed."],
    };
  }

  /**
   * The plan this page joins, chosen by the person looking at the finding.
   *
   * `willPublish` is the SAME condition the capture itself applies, which is
   * both sections being able to publish, so the words on this dialog and the
   * destination the piece ends up with cannot contradict each other. It is a
   * prediction from two flags the answer already carries, and the final word is
   * the sentence the endpoint sends back, which is what the line shows after.
   *
   * Nothing is written until a plan is pressed. The new plan path writes a plan
   * and then captures into it, in that order, and a capture that then refuses
   * leaves the plan it created standing and visible rather than disappearing
   * with the dialog.
   */
  function PlanPicker({ gap, siteId, sectionName, blockers, measure, onCaptured, onStale, onClose }) {
    // No blocker means both sections can publish, which is the condition the
    // capture itself applies. The blockers are the sentences for the halves
    // that failed, built where the sections are known rather than here.
    const willPublish = !(blockers || []).length;
    const DS = window.HuddleDesignSystem_775113;
    const [plans, setPlans] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [busy, setBusy] = useState(null);         // the plan being written into, or "new"
    const [refusal, setRefusal] = useState(null);
    const [title, setTitle] = useState(NEW_PLAN_TITLE);

    const loadPlans = useCallback(async () => {
      setPlans(null); setLoadErr(null);
      try {
        const rows = await api(`/content-plans?site_id=${siteId}`);
        setPlans(Array.isArray(rows) ? rows : []);
      } catch (e) {
        // Kept as a failure. An empty list would read as "this section has no
        // plans", and the only move it offers is creating one, which is the
        // wrong move when the plans are there and the read broke.
        setLoadErr(e.message || "The plans on this section could not be read.");
      }
    }, [siteId]);
    useEffect(() => { loadPlans(); }, [loadPlans]);

    // Escape closes, the same way the drawers on this screen do.
    useEffect(() => {
      const onKey = (ev) => { if (ev.key === "Escape" && !busy) onClose(); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [busy, onClose]);

    /* The one write that makes a piece. There is no other path to one on this
       screen, and the response is reported rather than interpreted: whether a
       piece was created or already existed is the endpoint's answer, not a
       guess from a status code here. */
    const capture = async (planId, planTitle, extra) => {
      setBusy(planId); setRefusal(null);
      try {
        const res = await api(`/content-plans/${planId}/items/from-gap`, {
          method: "POST",
          body: JSON.stringify({ site_id: siteId, gap_key: gap.key }),
        });
        /* WHICH plan it landed in, which is not always the one just pressed:
           one missing page is one piece for the whole section, so a page
           already captured into another plan comes back as that piece. Saying
           the plan that was pressed would name the wrong plan in the only case
           where the difference matters. */
        const known = [...(extra || []), ...(plans || [])];
        const landed = Number(res.item && res.item.plan_id);
        const row = known.find(p => Number(p.id) === landed);
        const name = (row && row.title) || (landed === Number(planId) ? planTitle : null);
        // Named when it can be named, and described when it cannot. A plan id
        // is not a name a customer has ever seen.
        onCaptured(res, name ? `the plan "${name}"` : `another plan on ${sectionName}`);
      } catch (e) {
        const r = refusalFor(e, sectionName);
        setRefusal(r);
        if (r.reloadPlans) loadPlans();
      } finally { setBusy(null); }
    };

    const startPlan = async () => {
      const name = title.trim();
      if (!name) return;
      setBusy("new"); setRefusal(null);
      let made = null;
      try {
        made = await api("/content-plans", { method: "POST", body: JSON.stringify({ site_id: siteId, title: name }) });
      } catch (e) {
        setBusy(null);
        setRefusal({
          title: "The plan was not created, so nothing was planned",
          lines: [e.message || "The plan could not be created."],
        });
        return;
      }
      // The plan exists from here on whatever the capture does, so it joins the
      // list before the capture runs rather than after it succeeds.
      setPlans(ps => [made, ...(ps || [])]);
      await capture(Number(made.id), made.title || name, [made]);
    };

    const body = (
      <div style={{ paddingBottom: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        <p className="ds-card__detail" style={{ margin: 0 }}>
          {gapSentence(gap)}. It becomes one piece of a plan on {sectionName}, carrying the evidence this finding
          rests on, and
          {willPublish
            ? " it can be published from MarketLens once it is written."
            : ` its deliverable is a brief for whoever builds the site, because ${blockers.join(", and ")}. Nothing`
              + " here will publish it."}
        </p>
        {gap.site_name && gap.site_name !== sectionName && (
          <span className="ds-card__note">
            The evidence for this finding sits on {gap.site_name}, and the piece is written onto {sectionName}, which is
            the section this screen is reading. To plan it onto another piece of this website, switch section at the top
            and open the Coverage Map there.
          </span>
        )}

        {refusal && (
          <div className="ds-strip ds-strip--error" style={{ display: "block" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <I name="alert-triangle" size={14} /><b>{refusal.title}</b>
            </div>
            {refusal.lines.map((l, i) => <p className="ds-card__detail" key={i}>{l}</p>)}
            {refusal.note ? <span className="ds-card__note">What the check said: {refusal.note}</span> : null}
            {refusal.stale && (
              <div className="ds-card__foot">
                <button className="bi-btn bi-btn--sm" onClick={() => { onStale(); onClose(); }}>
                  <I name="refresh" size={13} />Read the list again
                </button>
              </div>
            )}
          </div>
        )}

        {/* The plans on this section. Each one carries what it already holds,
            from the list endpoint's own roll-up, so picking is not a guess
            between identical names. */}
        {loadErr ? (
          <div className="ds-strip ds-strip--error">
            <I name="alert-triangle" size={14} />
            <span>The plans on {sectionName} could not be read: {loadErr}</span>
            <button className="bi-btn bi-btn--sm" onClick={loadPlans}>Try again</button>
          </div>
        ) : plans === null ? (
          <div className="ds-strip ds-strip--quiet"><I name="loader" size={14} />Reading the plans on {sectionName}...</div>
        ) : plans.length === 0 ? (
          <div className="ds-strip ds-strip--quiet">
            <I name="info" size={14} />
            <span>{sectionName} has no content plan yet, so there is nothing to add this to. Name one below and it
              becomes the first.</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
            <span className="ds-card__note">Pick the plan it joins, out of the plans on {sectionName}.</span>
            {plans.map(p => (
              <button className="bi-btn" key={p.id} disabled={!!busy}
                      style={{ width: "100%", justifyContent: "space-between", textAlign: "left" }}
                      onClick={() => capture(Number(p.id), p.title)}>
                <span>{p.title}</span>
                <span className="ds-card__note" style={{ marginLeft: 10 }}>
                  {busy === Number(p.id)
                    ? "planning it..."
                    : `${plural((p.items && p.items.pieces) || 0, "piece", "pieces")} · ${STATUS_WORD[p.status] || p.status}`}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* A new plan, named out loud. The only reason this is here and not in
            the Content Planner alone is that a section with no plan has nowhere
            to put the finding, and sending somebody away to make one and come
            back is how a finding gets forgotten. */}
        <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="ds-card__note">
            Or start a plan for it. Nothing is created until you press the button, and the name is the one in the box.
          </span>
          <input className="bm-field" value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="Name for the new plan" disabled={!!busy} />
          <button className="bi-btn bi-btn--primary" disabled={!title.trim() || !!busy} onClick={startPlan}>
            <I name="file-text" size={13} />
            {busy === "new" ? "Creating the plan..." : `Create "${title.trim()}" and plan this page into it`}
          </button>
        </div>

        <span className="ds-card__note">How a gap is decided: {measure}</span>
      </div>
    );

    const head = `Plan ${willPublish ? "this page" : "the handover brief"}`;
    const Dialog = DS && DS.Dialog;
    // No Dialog in the bundle is index.html's problem, but a button that opens
    // nothing must not be this component's answer to it: the chooser renders
    // inline instead, which is the same fallback the drawers here use.
    return Dialog
      ? React.createElement(Dialog, {
        title: head, description: gap.title, icon: <I name="file-text" size={18} />,
        onClose: busy ? undefined : onClose, open: true,
        footer: <button className="bi-btn" onClick={onClose} disabled={!!busy}>Cancel</button>,
      }, body)
      : (
        <div className="ds-strip">
          <b>{head}</b>
          {body}
          <button className="bi-btn" onClick={onClose} disabled={!!busy}>Cancel</button>
        </div>
      );
  }

  /* What became of the finding, in the drawer the line opens. Every sentence
     with a judgement in it is the endpoint's: whether a piece was created or
     already existed, what it delivers and why. The counts are the ones the
     write reported rather than the ones the request asked for. */
  function plannedRecord(o) {
    const ev = o.evidence || {};
    const src = ev.sources || {};
    const notClaimed = (ev.not_claimed || []).length;
    return (
      <div className="pg-rec" style={{ marginBottom: 16 }}>
        <div className="pg-rec__row">
          <span className="pg-rec__k">What happened</span>
          <span className="pg-rec__v">
            {o.created
              ? "This finding became a piece of a plan."
              : "This finding was already a piece of a plan, so nothing new was created."}
            {o.why ? <span className="pg-rec__note">{o.why}</span> : null}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Where it is</span>
          <span className="pg-rec__v">
            {o.plan_label}
            {o.status ? <span className="pg-rec__note">The piece is {STATUS_WORD[o.status] || o.status}.</span> : null}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">What it delivers</span>
          <span className="pg-rec__v">
            {o.destination && o.destination.publishes ? "A page to publish" : "A brief for whoever builds the site"}
            {o.destination && o.destination.why ? <span className="pg-rec__note">{o.destination.why}</span> : null}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Evidence kept with it</span>
          <span className="pg-rec__v">
            {src.written == null ? "Not reported" : plural(src.written, "named thing", "named things")}
            {ev.sources_error
              ? <span className="pg-rec__note">
                  The piece exists and carries its written summary, but the named evidence behind it was not stored.
                  Planning this page again creates nothing new, so it is safe to try once more.
                </span>
              : <span className="pg-rec__note">
                  Each one is something this finding rests on, stored with the piece so a reader months from now can
                  see why the page was planned.
                </span>}
            {ev.claimed && ev.claimed.keywords
              ? <span className="pg-rec__note">
                  {plural(ev.claimed.keywords, "keyword", "keywords")} of this section's own bank now count as material
                  this piece consumes.
                </span>
              : null}
            {notClaimed
              ? <span className="pg-rec__note">
                  {plural(notClaimed, "item", "items")} of the evidence is recorded on the piece by name instead: they
                  are not keywords this section's own bank holds, so nothing can hand them over as material.
                </span>
              : null}
          </span>
        </div>
      </div>
    );
  }

  /* The toast. Three outcomes and three sentences: a piece that can be
     published, a piece whose deliverable is a brief, and a page that was
     already planned, which is neither a failure nor a second success. */
  function plannedFlash(g, o) {
    if (!o.created) {
      return `"${g.title}" is already planned: it is a piece of ${o.plan_label}`
        + `${o.status ? `, ${STATUS_WORD[o.status] || o.status}` : ""}. Nothing new was created.`;
    }
    return `"${g.title}" is now a piece of ${o.plan_label}. `
      + (o.destination && o.destination.publishes
        ? "It can be published from MarketLens once it is written."
        : "Its deliverable is a brief for whoever builds the site, so nothing here will publish it.");
  }

  /* The full record, in the drawer. The `why` sentence is the endpoint's own
     and is shown word for word: it names the numbers the finding rests on, and
     rewriting it here would be a second version of the claim. */
  function gapRecord(g, measure, planCan, sectionName) {
    const ev = g.evidence || [];
    return (
      <div className="pg-rec">
        <p className="ds-card__detail">{g.why}</p>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Evidence</span>
          <span className="pg-rec__v">
            <span className="ds-card__sample">{ev.map(evidenceChip)}</span>
            {g.kind === "cluster" && g.keyword_count > ev.length && (
              <span className="pg-rec__note">
                The {ev.length} strongest of {plural(g.keyword_count, "keyword", "keywords")} in the cluster.{" "}
                {g.volume_known
                  ? `${g.volume_known} of them carry a known search volume.`
                  : "None of them carries a known search volume, so the demand behind this is the number of keywords and nothing more."}
              </span>
            )}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Kind of page</span>
          <span className="pg-rec__v">
            {PAGE_TYPE[g.page_type] || g.page_type}
            {g.page_type === "other" && (
              <span className="pg-rec__note">
                The page that already exists could be anything, so nothing here names a type it did not read.
              </span>
            )}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">{g.suggested_path ? "Suggested path" : "Suggested slug"}</span>
          <span className="pg-rec__v">{g.suggested_path || g.slug}</span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Which site</span>
          <span className="pg-rec__v">
            {g.site_name || "Unknown"}{g.site_source ? ` (${g.site_source})` : ""}
            <span className="pg-rec__note">{g.why_site}</span>
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">What can be done</span>
          <span className="pg-rec__v"><span className="pg-rec__note">{deliverableText(g, planCan, sectionName)}</span></span>
        </div>
        {!g.certain && (
          <div className="pg-rec__row">
            <span className="pg-rec__k">How settled this is</span>
            <span className="pg-rec__v">
              A candidate
              <span className="pg-rec__note">
                The page list this was measured against is incomplete, so it can only prove a floor: this page may
                already exist on a part of the website nothing has read yet.
              </span>
            </span>
          </div>
        )}
        <div className="pg-rec__row">
          <span className="pg-rec__k">How a gap is decided</span>
          <span className="pg-rec__v"><span className="pg-rec__note">{measure}</span></span>
        </div>
      </div>
    );
  }

  /* What the answer was measured against, which is the part that decides what
     every figure above means. One line, because it is five sentences and a
     table that nobody needs until they doubt the answer. */
  function universeRecord(d) {
    const u = d.universe || {};
    const per = u.per_site || [];
    return (
      <div className="pg-rec">
        <p className="ds-card__detail">
          {d.scope === "property"
            ? `Measured across every piece of ${d.property ? d.property.name : "this website"} at once, because the banks and `
              + "the pages routinely sit on different pieces of one website: a cluster recorded on a blog can be answered "
              + "by a page on the marketing site, and asking one piece on its own reports that page as missing."
            : "Measured over this site alone, because it is not grouped under a property yet."}
        </p>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Pages read</span>
          <span className="pg-rec__v">
            {plural(u.count || 0, "page", "pages")}
            {u.read_at ? <span className="pg-rec__note">Last read {ago(u.read_at)}.</span> : null}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Where from</span>
          <span className="pg-rec__v">
            {(u.sources || []).length
              ? (u.sources || []).map(s => (s === "crawl" ? "a crawl of the public site" : "the coverage scan of a CMS")).join(" and ")
              : "nowhere yet"}
          </span>
        </div>
        <div className="pg-rec__row">
          <span className="pg-rec__k">Complete</span>
          <span className="pg-rec__v">
            {u.complete ? "Yes" : "No"}
            {u.why ? <span className="pg-rec__note">{u.why}</span> : null}
          </span>
        </div>
        {per.map(p => (
          <div className="pg-rec__row" key={p.site_id}>
            <span className="pg-rec__k">{p.name}</span>
            <span className="pg-rec__v">
              {plural(p.pages || 0, "page", "pages")}
              {p.why ? <span className="pg-rec__note">{p.why}</span> : null}
              {p.no_public_path
                ? <span className="pg-rec__note">
                    {plural(p.no_public_path, "page", "pages")} of it take no part in the language comparison: this piece
                    has not declared where it publishes, so what their public address is cannot be known.
                  </span>
                : null}
            </span>
          </div>
        ))}
        <div className="pg-rec__row">
          <span className="pg-rec__k">How a gap is decided</span>
          <span className="pg-rec__v"><span className="pg-rec__note">{d.measure}</span></span>
        </div>
      </div>
    );
  }

  /* How the language comparison went, whether or not it produced anything. It
     is reported in both cases on purpose: "no language gaps" and "the paths
     could not be compared" are different answers, the endpoint distinguishes
     them, so the screen has to as well. */
  function marketRecord(m) {
    return (
      <div className="pg-rec">
        {m.why ? <p className="ds-card__detail">{m.why}</p> : null}
        <div className="pg-rec__row">
          <span className="pg-rec__k">Prefixes seen</span>
          <span className="pg-rec__v">
            {(m.prefixes || []).length
              ? <span className="ds-card__sample">
                  {(m.prefixes || []).map(p => (
                    <span className="ds-chip" key={p.prefix}>
                      {p.prefix} ({plural(p.pages, "page", "pages")}){p.served ? "" : ", too few to read as a language"}
                    </span>
                  ))}
                </span>
              : "None. No path on this website begins with something shaped like a language tag."}
          </span>
        </div>
        {(m.pairs || []).map((p, i) => (
          <div className="pg-rec__row" key={i}>
            <span className="pg-rec__k">{(p.prefixes || []).join(" against ")}</span>
            <span className="pg-rec__v">
              {p.shared_paths} shared {p.shared_paths === 1 ? "path" : "paths"}, parity {p.parity}
              {p.comparable ? ", comparable" : ", not comparable"}
              <span className="pg-rec__note">{p.why}</span>
            </span>
          </div>
        ))}
        {m.parity_min != null && (
          <div className="pg-rec__row">
            <span className="pg-rec__k">The threshold</span>
            <span className="pg-rec__v"><span className="pg-rec__note">
              At least {Math.round(m.parity_min * 100)}% of the paths the smaller side serves have to be served under
              both prefixes before a path present under one and absent under the other is read as a missing page. Below
              that, the website localises its paths and comparing them would report every page as missing twice.
            </span></span>
          </div>
        )}
        {m.excluded ? (
          <div className="pg-rec__row">
            <span className="pg-rec__k">Pages left out</span>
            <span className="pg-rec__v">
              {plural(m.excluded, "page", "pages")}
              <span className="pg-rec__note">
                The piece serving them has not declared a publish address, so the path stored for them is the one their
                content is edited at rather than the one the public visits. Declaring it in the property setup is the
                only thing that fixes this.
              </span>
            </span>
          </div>
        ) : null}
      </div>
    );
  }

  /* What a kind checked, said in every state rather than only when it found
     nothing. A zero with nothing behind it reads like a source that did not
     run, and these sentences are the counts that tell the two apart. */
  function kindChecked(kind, s) {
    if (!s) return "This source did not report.";
    if (s.status === "unknown") return s.why || "Nothing could be measured, so nothing is claimed.";
    if (kind === "cluster") {
      if (s.status === "no_clusters") {
        return "No keyword cluster is recorded anywhere in this website, so this half of the answer is empty rather "
          + "than clean. Clusters come from the Keywords Planner.";
      }
      const parts = [`${plural(s.clusters_total, "cluster", "clusters")} checked`];
      if (s.clusters_covered) parts.push(`${s.clusters_covered} already have a page`);
      if (s.clusters_too_small) parts.push(`${s.clusters_too_small} too small to count as a cluster`);
      if (s.clusters_untestable) parts.push(`${s.clusters_untestable} with no keyword long enough to look for safely`);
      return parts.join(", ") + ".";
    }
    if (kind === "offering") {
      if (s.status === "no_offerings") {
        return "No product or service is recorded as this business's own anywhere in this website, so this half of the "
          + "answer is empty rather than clean. Offerings come from the Products register.";
      }
      const parts = [`${plural(s.offerings_total, "offering", "offerings")} checked`];
      if (s.offerings_covered) parts.push(`${s.offerings_covered} already named by a page`);
      if (s.offerings_untestable) parts.push(`${s.offerings_untestable} with a name too short to look for safely`);
      return parts.join(", ") + ".";
    }
    const served = (s.prefixes || []).filter(p => p.served).length;
    if (s.status === "ok") return `${plural(served, "language prefix", "language prefixes")} compared path by path.`;
    return s.why || "The language comparison did not run.";
  }

  /* The line that stands in for the language comparison's own account of
     itself. Its title is the outcome; the pairs, the prefixes and the threshold
     behind it are in the drawer. */
  function marketMethodTitle(m) {
    const served = (m.prefixes || []).filter(p => p.served).length;
    if (m.status === "ok") return `The language comparison ran over ${plural(served, "prefix", "prefixes")} this website serves`;
    if (m.status === "single_prefix") return "Nothing to compare: this website serves one language prefix, not two";
    if (m.status === "not_comparable") return "The languages name their pages differently, so no counterpart can be found by path";
    return "How the language comparison went";
  }

  // How many lines a kind shows before asking. Ten, because the list is sorted
  // by the strength of its evidence and the eleventh is rarely the one acted
  // on first; the rest are one click away and the count never hides.
  const PG_SHOW = 10;

  /* The icon on a kind that found nothing, and it is two icons rather than one
     because a zero means two different things. 'none' and 'ok' are findings:
     the source ran, had something to measure and came back clean. Every other
     status is an admission: an empty register, a comparison that could not be
     made. A tick over the second kind would turn "we could not look" into "you
     are fine", which is the mistake the whole section is built to avoid. */
  const kindZeroIcon = (s) => (s && (s.status === "none" || s.status === "ok") ? "check" : "info");

  /**
   * The section. Its own read, its own three states, and its own failure.
   *
   * `onFlash` is the host screen's toast, passed in rather than rebuilt, so the
   * one place that says whether a copy worked is the place every other message
   * on this screen appears.
   */
  function PageGaps({ site, go, onFlash, reload }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [open, setOpen] = useState(true);
    const [expand, setExpand] = useState({});
    // The gap waiting for a plan to be picked, and what the endpoint answered
    // about the ones already planned, keyed by the gap's own key.
    //
    // This map starts EMPTY on every load and is never pre-filled, because
    // nothing here can know what is already planned without asking: there is
    // no read that joins a plan's pieces back to the gaps they answer. So the
    // screen does not claim to know. Pressing the action asks, and a page that
    // was already captured comes back as the piece that exists, which is the
    // endpoint's answer and not a guess made here.
    const [pick, setPick] = useState(null);
    const [planned, setPlanned] = useState({});

    const load = useCallback(async () => {
      if (siteId == null) { setState({ loading: false, error: null, data: null }); return; }
      setState(s => ({ ...s, loading: true, error: null }));
      try {
        setState({ loading: false, error: null, data: await api(`/coverage/page-gaps?site_id=${siteId}`) });
      } catch (e) {
        // Kept as a failure. Rendered as an empty list it would say "this
        // website is missing nothing", which is the one answer an outage must
        // never be allowed to produce here, and it is the same reason the route
        // itself refuses to swallow its own errors.
        setState({ loading: false, error: e.message || "Could not work out what pages are missing.", data: null });
      }
      // `reload` is a refresh token and is read nowhere else on purpose: the
      // coverage scan this screen runs is what writes site_pages, which IS the
      // page universe for a connected site, so a scan that finds 40 new pages
      // changes this answer. Without it the section would keep showing gaps
      // measured against the page list from before the scan, which is the same
      // class of lie as a stale "nothing found".
    }, [siteId, reload]);
    useEffect(() => { load(); }, [load]);
    /* A different section's plans are not this one's. What was planned is
       recorded against the section it was written onto, so switching section
       drops it rather than carrying a claim about plan "Q4" onto a piece of the
       website that has no such plan. A reload of the SAME section keeps it: a
       captured page is still a missing page until somebody writes it, so the
       gap comes back and the piece it became still exists. */
    useEffect(() => { setPlanned({}); setPick(null); }, [siteId]);

    const d = state.data;
    const u = (d && d.universe) || {};

    /* The clipboard, which is still its own move and not a worse version of
       planning it. A brief goes to a developer, to a contractor, into an email;
       a plan keeps the finding inside the app with its evidence attached. There
       is still no route anywhere that turns a gap into a published page, so
       neither button claims one. */
    const copyBrief = async (g) => {
      try {
        if (!navigator.clipboard) throw new Error("This browser will not give the page clipboard access.");
        await navigator.clipboard.writeText(briefOf(g, d.measure));
        onFlash && onFlash(g.can_create
          ? `Brief for "${g.title}" copied. Plan it, on the line, keeps the same finding in a plan without retyping it.`
          : `Brief for "${g.title}" copied. ${g.site_name || "This site"} has no CMS, so this page has to be built by whoever builds the site.`);
      } catch (e) {
        onFlash && onFlash(e.message || "Could not copy the brief.");
      }
    };

    const head = (sub) => (
      <button className="ds-strip__head" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <I name={open ? "chevron-down" : "chevron-right"} size={15} />
        <span className="ds-strip__title">Pages this website does not have</span>
        <span className="ds-strip__sub">{sub}</span>
      </button>
    );

    if (siteId == null) return null;

    if (state.loading && !d) {
      return (
        <div className="ds-strip pg-sec">
          {head("reading")}
          {open && <div className="ds-strip--quiet"><I name="loader" size={14} />Working out which pages are missing...</div>}
        </div>
      );
    }
    if (state.error) {
      return (
        <div className="ds-strip pg-sec">
          {head("could not be read")}
          {open && (
            <div className="ds-strip--error">
              <I name="alert-triangle" size={14} />
              <span>
                Could not work out what pages are missing: {state.error} Nothing is reported either way, because an
                empty list here would read as "this website is missing nothing".
              </span>
              <button className="bi-btn bi-btn--sm" onClick={load}>Try again</button>
            </div>
          )}
        </div>
      );
    }
    if (!d) return null;

    const where = d.scope === "property" && d.property ? d.property.name : (site && site.name) || "this site";
    const notes = d.notes || [];

    /* STATE 1. UNKNOWN. The page list could not be read, so nothing is
       reported, and the reason IS the content of this state rather than a
       footnote under a list of zeroes. The endpoint's own sentences are used
       word for word: they name which piece contributed nothing and what each
       one is waiting for. */
    if (u.known === false) {
      return (
        <div className="ds-strip pg-sec">
          {head("no page list yet, so nothing is reported")}
          {open && (
            <div className="ds-cards">
              <div className="ds-card ds-card--todo">
                <div className="ds-card__head">
                  <span className="ds-card__icon"><I name="alert-triangle" size={15} /></span>
                  <span className="ds-card__title">No page list for {where}, so nothing can be reported as missing</span>
                </div>
                {notes.length
                  ? notes.map((n, i) => <p className="ds-card__detail" key={i}>{n}</p>)
                  : <p className="ds-card__detail">{u.why}</p>}
                <p className="ds-card__detail">
                  So this is an absence of data and not a clean bill of health. No gap is listed below, because a list
                  computed against no pages would be every cluster and every offering in your registers, which would
                  tell you to rebuild a website you already have.
                </p>
                {(u.per_site || []).length ? (
                  <div className="pg-rec">
                    {(u.per_site || []).map(p => (
                      <div className="pg-rec__row" key={p.site_id}>
                        <span className="pg-rec__k">{p.name}</span>
                        <span className="pg-rec__v">
                          {plural(p.pages || 0, "page", "pages")}
                          {p.why ? <span className="pg-rec__note">{p.why}</span> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="ds-card__foot">
                  <span className="ds-card__note">
                    Scan the site, above, fills the half of this that a connected CMS holds. A piece with no CMS is
                    filled by its own crawl, which lives in the property's setup.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    const total = d.gaps_total || 0;
    const byKind = d.by_kind || {};
    const sources = d.sources || {};
    const gaps = d.gaps || [];

    /* THE SECTION A PLAN WOULD BE WRITTEN ONTO, read off the answer's own site
       list rather than decided here. It is the same value the capture reads for
       the same purpose, so the label on the line and the destination the piece
       ends up with cannot disagree about one website.

       A section the answer did not carry leaves this false, which is "nothing
       can claim it publishes" and not "it cannot publish": the capture takes
       the identical position for the identical reason, and the sentence it
       sends back says which half was missing. */
    const planSite = (d.sites || []).find(s => Number(s.site_id) === Number(siteId));
    const planCan = !!(planSite && planSite.can_publish);
    const sectionName = (planSite && planSite.name) || (site && site.name) || "this section";
    /* WHY a piece planned here could not be published, one sentence per half
       that failed, and empty when neither did.

       BOTH sections have to be able to publish, which is the condition the
       capture applies and not a looser one. The gap's half is where its
       evidence sits and, for a language gap, where the page belongs; the plan's
       half is the connection a publish would go THROUGH. Either one read by a
       crawl means no publish is possible, so the button says brief rather than
       page, and the sentence names which half rather than leaving it to be
       guessed. A section nothing could identify reads as "nothing can claim it
       publishes" and never as "it cannot". */
    const briefBlockers = (g) => {
      const halves = [];
      if (!g.can_create) {
        halves.push(g.site_name
          ? `${g.site_name} is read by crawling it and has no CMS behind it`
          : "the section this finding belongs to is not known, so nothing can claim it has a CMS");
      }
      if (!planCan) {
        halves.push(planSite
          ? `${sectionName}, which is the section a plan here is written onto, is read by crawling it and has no CMS behind it`
          : `${sectionName} could not be found among this website's pieces, so nothing can claim it has a CMS`);
      }
      return halves;
    };
    const willPublish = (g) => briefBlockers(g).length === 0;

    /* What the endpoint answered about one gap, kept so the line can stop
       inviting the same action. Nothing is derived from the status code: the
       response says whether a piece was created or already existed, which plan
       it is in and what it delivers, and all four are stored as given. */
    const recordCapture = (g, res, planLabel) => {
      const o = {
        created: !!res.created,
        plan_id: Number(res.item && res.item.plan_id) || null,
        plan_label: planLabel,
        status: (res.item && res.item.status) || null,
        destination: res.destination || null,
        why: res.why || null,
        evidence: res.evidence || null,
      };
      setPlanned(p => ({ ...p, [g.key]: o }));
      setPick(null);
      onFlash && onFlash(plannedFlash(g, o));
    };

    const found = total === 0
      ? (u.complete === false ? "no gap found, over an incomplete page list" : "nothing missing")
      : (u.complete === false
        ? `${total} possibly missing, over an incomplete page list`
        : plural(total, "page missing", "pages missing"));
    const sub = `${where} · ${found}`
      + ` · measured against ${plural(u.count || 0, "page", "pages")}${u.read_at ? `, read ${ago(u.read_at)}` : ""}`;

    return (
      <div className="ds-strip pg-sec">
        {head(sub)}
        {!open ? null : (
          <>
            <div className="ds-lines">
              {/* STATE 2. INCOMPLETE. The gaps are shown AND the answer says it
                  is partial, before the list rather than under it, because a
                  caveat below the answer is a caveat nobody reads. The notes
                  carry the endpoint's own reasons, including which piece of the
                  website came up short. */}
              {u.complete === false && (
                <DetailLine tone="problem"
                  title="This answer is partial: the page list it was measured against is incomplete"
                  subtitle="Why, and what it means for the gaps below">
                  {notes.map((n, i) => <p className="ds-card__detail" key={i}>{n}</p>)}
                  <p className="ds-card__detail">
                    An incomplete page list can only prove a floor, so every gap below is a candidate rather than a
                    settled finding: the page may exist on a part of this website nothing has read yet. Gaps may be
                    missing from the list for the same reason.
                  </p>
                </DetailLine>
              )}
              {/* The caveats, when the answer is otherwise whole. One line and
                  not five strips: these are things the answer cannot promise,
                  and a stack of red bars would make them read as failures. */}
              {u.complete !== false && notes.length > 0 && (
                <DetailLine tone="todo"
                  title={`${plural(notes.length, "thing", "things")} this answer cannot promise`}
                  subtitle="Worth reading before acting on the list">
                  {notes.map((n, i) => <p className="ds-card__detail" key={i}>{n}</p>)}
                </DetailLine>
              )}
              <DetailLine tone="neutral"
                title={`Measured against ${plural(u.count || 0, "page", "pages")} across ${plural((u.per_site || []).length, "piece", "pieces")} of this website`}
                subtitle="What the answer was computed from">
                {universeRecord(d)}
              </DetailLine>
            </div>

            {/* STATE 3. NOTHING FOUND, which is two answers and not one. Over
                an incomplete page list a zero proves nothing, so it is reported
                as the hole it is; over a complete one it is good news and is
                said warmly, against the counts it checked, because "no gaps" on
                its own is also what a feature that did not run looks like. */}
            {total === 0 && u.complete === false && (
              <div className="ds-strip--quiet">
                <I name="info" size={14} />
                <span>
                  No gap was found, and that is not a clean bill of health: the page list this was measured against is
                  incomplete, so a page that should exist can be missing from the answer the same way it is missing from
                  the list. The line above says which piece came up short.
                </span>
              </div>
            )}
            {total === 0 && u.complete !== false && (
              <div className="ds-strip--quiet pg-clean">
                <I name="check-circle" size={14} />
                <span>
                  Nothing is missing that this can see. The check ran over {plural(u.count || 0, "page", "pages")} of
                  this website, against {plural((sources.cluster && sources.cluster.clusters_total) || 0, "keyword cluster", "keyword clusters")}
                  {" "}and {plural((sources.offering && sources.offering.offerings_total) || 0, "offering", "offerings")} in your
                  registers, and each kind below says what that came to.
                </span>
              </div>
            )}

            {GAP_KIND_ORDER.map(kind => {
              const meta = GAP_KINDS[kind];
              const count = byKind[kind] || 0;
              const mine = gaps.filter(g => g.kind === kind);
              const shown = expand[kind] ? mine : mine.slice(0, PG_SHOW);
              const stat = sources[kind];
              return (
                <div className="pg-kind" key={kind}>
                  <div className="pg-kind__head">
                    <span className="ds-card__icon"><I name={meta.icon} size={15} /></span>
                    <span className="pg-kind__label">{meta.label}</span>
                    {/* The count stays on a kind that found nothing. It is the
                        difference between "checked, nothing missing" and
                        "nobody looked", and only one of those is good news. */}
                    <span className="pg-kind__count">{count}</span>
                  </div>
                  <div className="ds-lines">
                    {shown.map(g => {
                      // What the endpoint answered about this gap, if it has
                      // been asked. It changes the line's tone, its title, and
                      // which action it offers.
                      const o = planned[g.key];
                      return (
                      <DetailLine key={g.key} tone={o ? "opportunity" : meta.tone}
                        /* Planned, and still a candidate when the page list
                           was incomplete: both facts survive, because having
                           planned it does not make it certain the website is
                           missing it. */
                        title={(o
                          ? `${o.created ? "Planned" : "Already planned"}${g.certain ? ": " : ", though possibly not missing: "}`
                          : (g.certain ? "" : "Possibly missing: ")) + gapSentence(g)}
                        subtitle={`${PAGE_TYPE[g.page_type] || g.page_type} · ${g.site_name || "site unknown"}`
                          + (o ? ` · in ${o.plan_label}` : "")}
                        action={
                          /* The actions the deliverable allows, and only those.
                             The clipboard is always one of them. The second one
                             captures the finding into a plan, and its label is
                             the DELIVERABLE rather than the verb: a section
                             read by crawling it cannot be published to by
                             anything here, so what the piece becomes is a brief
                             and the button says brief.

                             Once it is planned, the planning action goes. A
                             button repeating work that is already done would be
                             inviting the same move as if nothing had happened,
                             so what stands in its place is the one move that is
                             left and really goes somewhere. */
                          <span className="pg-acts">
                            <button className="bi-btn bi-btn--sm" onClick={() => copyBrief(g)}
                                    title={g.can_create
                                      ? "Put this whole brief on the clipboard"
                                      : `${g.site_name || "This site"} has no CMS, so this page has to be built by whoever builds the site.`}>
                              <I name="copy" size={13} />{g.can_create ? "Copy brief" : "Copy the handover brief"}
                            </button>
                            {o
                              ? (go && (
                                <button className="bi-btn bi-btn--sm" onClick={() => go("content-planner")}
                                        title={`Open the Content Planner, where ${o.plan_label} holds this piece.`}>
                                  <I name="arrow-up-right" size={13} />Open the Content Planner
                                </button>
                              ))
                              : (
                                <button className="bi-btn bi-btn--sm" onClick={() => setPick(g)}
                                        title={willPublish(g)
                                          ? `Make this a piece of a plan on ${sectionName}, with its evidence attached.`
                                          : `Make this a piece of a plan on ${sectionName}. Its deliverable is a brief for whoever builds the site, not a published page.`}>
                                  <I name="file-text" size={13} />{willPublish(g) ? "Plan it" : "Plan the handover brief"}
                                </button>
                              )}
                          </span>
                        }>
                        {o ? plannedRecord(o) : null}
                        {gapRecord(g, d.measure, planCan, sectionName)}
                      </DetailLine>
                      );
                    })}
                    {/* The language comparison accounts for itself whether or
                        not it found anything, because "none missing" and "the
                        paths cannot be compared" are different answers. */}
                    {kind === "market" && stat && ((stat.prefixes || []).length || (stat.pairs || []).length || stat.why) ? (
                      <DetailLine tone="neutral" title={marketMethodTitle(stat)} subtitle="How the comparison was made">
                        {marketRecord(stat)}
                      </DetailLine>
                    ) : null}
                    {count === 0 && <div className="ds-strip--quiet"><I name={kindZeroIcon(stat)} size={14} />{kindChecked(kind, stat)}</div>}
                  </div>
                  <div className="ds-card__foot">
                    {mine.length > PG_SHOW && (
                      <button className="bi-btn bi-btn--sm" onClick={() => setExpand(x => ({ ...x, [kind]: !x[kind] }))}>
                        {expand[kind] ? "Show fewer" : `Show all ${mine.length}`}
                      </button>
                    )}
                    {count > 0 && <span className="ds-card__note">{kindChecked(kind, stat)}</span>}
                    {/* The total is reported whole while the list is capped, so
                        a capped list is never read as the complete finding. */}
                    {count > mine.length && (
                      <span className="ds-card__note">
                        Showing {mine.length} of {count}: the answer carries a capped list per kind so one kind cannot
                        crowd out the other two.
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
        {/* The chooser, mounted once for the whole section rather than per
            line, and outside the fold so collapsing the section while it is
            open cannot leave a write half made. `onStale` is the answer to the
            two refusals that are about the LIST and not about this gap: the
            page list could not be read, or the gap is no longer reported. Both
            mean the lines on screen cannot be trusted, so the honest move is to
            read the whole answer again. */}
        {pick && (
          <PlanPicker gap={pick} siteId={siteId} sectionName={sectionName}
                      blockers={briefBlockers(pick)} measure={d.measure}
                      onCaptured={(res, planLabel) => recordCapture(pick, res, planLabel)}
                      onStale={load} onClose={() => setPick(null)} />
        )}
      </div>
    );
  }

  function CoverageMap({ site, go }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);

    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [band, setBand] = useState("all");
    const [kind, setKind] = useState("all");
    const [q, setQ] = useState("");
    const [scanning, setScanning] = useState(false);
    const [run, setRun] = useState(null);
    const [scanErr, setScanErr] = useState(null);
    // Bumped when something has changed what the page gaps would be measured
    // against, which is the scan and an explicit reload, and nothing else.
    const [gapSeq, setGapSeq] = useState(0);
    const [toast, setToast] = useState(null);
    const stopPoll = useRef(null);
    const toastT = useRef(null);

    const flash = (msg) => {
      setToast(msg);
      clearTimeout(toastT.current);
      toastT.current = setTimeout(() => setToast(null), 5200);
    };
    useEffect(() => () => {
      clearTimeout(toastT.current);
      if (typeof stopPoll.current === "function") stopPoll.current();
    }, []);

    const load = useCallback(async () => {
      if (siteId == null) { setState({ loading: false, error: null, data: null }); return; }
      setState(s => ({ ...s, loading: true, error: null }));
      try {
        setState({ loading: false, error: null, data: await api(`/coverage/taxonomy?site_id=${siteId}`) });
      } catch (e) {
        // Kept as an error rather than as empty data. A read that failed and a
        // taxonomy with no gaps must never render the same way.
        setState({ loading: false, error: e.message || "Could not read the coverage map.", data: null });
      }
    }, [siteId]);
    useEffect(() => { load(); }, [load]);
    // What the Reload button means: both answers on this screen, not just the
    // map. Kept apart from load() so the mount effect above does not fetch the
    // page gaps twice.
    const reloadAll = useCallback(() => { load(); setGapSeq(n => n + 1); }, [load]);

    // The scan behind the whole screen. Slow, because it pages through every
    // published post and page over the REST API, so it reports the server's own
    // milestones rather than a bar on a timer.
    const scan = useCallback(async () => {
      if (siteId == null || scanning) return;
      setScanning(true); setRun(null); setScanErr(null);
      const runId = newRunId();
      stopPoll.current = pollRun(runId, setRun);
      try {
        const res = await api(`/coverage/sync`, { method: "POST", body: JSON.stringify({ site_id: siteId, run_id: runId }) });
        stopPoll.current();
        await load();
        // The scan has just rewritten this site's page inventory, so the page
        // gaps below were measured against a list that no longer exists.
        setGapSeq(n => n + 1);
        const read = Number(res.scanned) || 0;
        const total = Number(res.site_total) || 0;
        // The server writes its own sentence for an empty or capped scan, so it
        // is used rather than saying the same thing again in different words.
        flash(`Read ${read} of ${plural(total, "published page", "published pages")}.${res.note ? " " + res.note : ""}`);
      } catch (e) {
        if (typeof stopPoll.current === "function") stopPoll.current();
        // The endpoint's messages are written for the user (unreachable host,
        // refused credentials, no known site URL), so they are shown verbatim
        // and they stay on screen instead of vanishing with a toast.
        const msg = e.message || "The coverage scan failed.";
        setScanErr(msg); flash(msg);
      } finally { setScanning(false); }
    }, [siteId, scanning, load]);

    const DS = window.HuddleDesignSystem_775113;
    const d = state.data;
    const terms = (d && d.terms) || [];
    const bands = (d && d.bands) || { none: 0, thin: 0, covered: 0, unknown: 0 };
    const needle = q.trim().toLowerCase();
    const shown = terms.filter(t =>
      (band === "all" || t.band === band) &&
      (kind === "all" || t.kind === kind) &&
      (!needle || String(t.name).toLowerCase().includes(needle)));

    const header = (
      <div className="bi-header">
        <div className="bi-header__titles">
          <h1 className="bi-title"><span className="bi-title__icon"><I name="layers" size={20} /></span>Coverage Map</h1>
          <div className="bi-subtitle">Where your categories and tags have content, and where they do not{site ? " · " + site.name : ""}</div>
        </div>
        <div className="bi-header-actions">
          <button className="bi-btn" onClick={reloadAll} disabled={state.loading}><I name="refresh" size={16} />Reload</button>
          <button className="bi-btn bi-btn--primary" onClick={scan} disabled={scanning || siteId == null}>
            {scanning
              ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Scanning…</>
              : <><I name="target" size={16} />Scan the site</>}
          </button>
        </div>
      </div>
    );

    const shell = (body) => (
      <div className="bi-page">
        {header}
        {scanErr && <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{scanErr}</span></div>}
        {body}
        {/* The second question, under the first. Inside shell() rather than in
            the final return, because the two halves are independent reads: a
            taxonomy that nobody has scanned, or a taxonomy read that failed,
            says nothing about whether the page gaps can be computed, and
            hiding them behind the first answer's empty state would throw away
            an answer this screen already has. Its own component, so its hooks
            are its own and the early returns above cannot reorder them. */}
        <PageGaps site={site} go={go} onFlash={flash} reload={gapSeq} />
        {toast && <div className="ml-ws-toast" role="status">{toast}</div>}
      </div>
    );

    if (scanning) return shell(<div className="ds-strip"><ScanProgress run={run} /></div>);
    if (state.loading && !d) return shell(<div className="ds-strip ds-strip--quiet"><I name="loader" size={14} />Reading the coverage map...</div>);
    if (state.error) {
      return shell(
        <div className="ds-strip ds-strip--error">
          <I name="alert-triangle" size={14} />
          <span>Could not read the coverage map: {state.error}</span>
          <button className="bi-btn bi-btn--sm" onClick={load}>Try again</button>
        </div>
      );
    }
    if (!d) return shell(<div className="ds-strip ds-strip--quiet"><I name="circle" size={14} />Pick a site to map.</div>);

    /* Nobody has read this site, so there are no counts, only terms. Shown as
       an absence of data and not as a grid of zeroes: a page of "Nothing found"
       rows would be a statement about content nobody has looked at. */
    if (d.never_scanned) {
      return shell(
        <div className="ds-cards">
          <div className="ds-card ds-card--todo">
            <div className="ds-card__head">
              <span className="ds-card__icon"><I name="alert-triangle" size={15} /></span>
              <span className="ds-card__title">No coverage map for this site yet</span>
            </div>
            <p className="ds-card__detail">
              Nothing has read this site, so nothing here knows which pages it publishes and no term below can be
              counted. Your taxonomy is {plural(d.terms_total, "term", "terms")} long; how much content sits under
              each of them is unknown, which is a different answer from none.
            </p>
            <div className="ds-card__foot">
              <button className="bi-btn bi-btn--sm" onClick={scan}>Scan the site</button>
              <span className="ds-card__note">reads every published post and page over the REST API</span>
            </div>
          </div>
        </div>
      );
    }

    /* Scanned, but there is no taxonomy to map. The fix is somewhere else
       entirely, so the card goes there rather than offering another scan. */
    if (!d.terms_total) {
      const readFailed = !d.vocabulary.categories.ok || !d.vocabulary.tags.ok;
      return shell(
        <div className="ds-cards">
          <div className="ds-card ds-card--todo">
            <div className="ds-card__head">
              <span className="ds-card__icon"><I name="alert-triangle" size={15} /></span>
              <span className="ds-card__title">{readFailed ? "The taxonomy could not be read" : "This site has no categories or tags"}</span>
            </div>
            <p className="ds-card__detail">
              {readFailed
                ? (d.notes || []).join(" ")
                : "There is nothing to draw a map over. The Taxonomy Library proposes categories and tags from what "
                  + "this site publishes and from its keyword and topic banks, and creates them in WordPress when you approve them."}
            </p>
            {!readFailed && go && (
              <div className="ds-card__foot">
                <button className="bi-btn bi-btn--sm" onClick={() => go("taxonomy-suggestions")}>Open the Taxonomy Library</button>
              </div>
            )}
          </div>
        </div>
      );
    }

    const scanned = d.scan ? `scanned ${ago(d.scan.at)}` : "scan date unknown";

    return shell(
      <>
        {/* Everything the map cannot promise, before the map itself rather than
            under it. A caveat below the answer is a caveat nobody reads. */}
        {(d.notes || []).map((n, i) => (
          <div className="ds-strip ds-strip--error" key={i}><I name="alert-triangle" size={14} /><span>{n}</span></div>
        ))}

        <p className="seo-intro">
          {plural(d.terms_total, "term", "terms")} in this site's taxonomy, counted against
          the {plural(d.pages_counted, "published page", "published pages")} the last scan stored ({scanned}).
          A page counts under a term when the term's name is in its title or its slug, so this measures how the
          site NAMES its content, not which terms WordPress filed each post under. Thin means {d.thin_at} pages or
          fewer.
        </p>

        <div className="bi-bulk-bar">
          <div className="kw-seg kw-seg--lg">
            <button className={band === "all" ? "on" : ""} onClick={() => setBand("all")}>All ({d.terms_total})</button>
            {BAND_ORDER.map(b => (
              <button key={b} className={band === b ? "on" : ""} onClick={() => setBand(b)}>{BANDS[b].label} ({bands[b] || 0})</button>
            ))}
          </div>
          <div className="kw-seg">
            {[["all", "Both"], ["category", "Categories"], ["tag", "Tags"]].map(([id, l]) => (
              <button key={id} className={kind === id ? "on" : ""} onClick={() => setKind(id)}>{l}</button>
            ))}
          </div>
          <input className="bm-field" style={{ maxWidth: 220 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Find a term" />
        </div>

        <div className="kw-table-wrap">
          <table className="kw-table">
            <thead><tr>
              <th>Term</th>
              <th style={{ width: 110 }}>Kind</th>
              <th style={{ width: 170 }}>From</th>
              <th style={{ width: 110 }}>Pages</th>
              <th style={{ width: 150 }}>Coverage</th>
              <th>What is under it</th>
            </tr></thead>
            <tbody>
              {shown.map(t => (
                <tr key={t.kind + ":" + t.name}>
                  <td><b>{t.name}</b></td>
                  <td>{t.kind === "tag" ? "Tag" : "Category"}</td>
                  <td style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>{SOURCE_LABEL[t.source] || t.source || "unknown"}</td>
                  <td>{countCell(t)}</td>
                  <td>{bandBadge(t.band)}</td>
                  <td>
                    {t.sample.length
                      ? <div className="ds-card__sample">
                          {t.sample.map(p => <PageChip key={p.post_id} page={p} />)}
                          {t.sample_of > t.sample.length && <span className="ds-chip ds-chip--more">and {t.sample_of - t.sample.length} more</span>}
                        </div>
                      // The reason a row has no examples is the finding, so it is
                      // written out rather than left as an empty cell.
                      : <span className="ds-card__note">{t.why || "No published page names this term."}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && (
            <div className="bi-subempty">
              {terms.length ? "No term matches this filter." : "No terms to show."}
            </div>
          )}
        </div>

        {DS && DS.Banner && bands.unknown > 0 && (
          <div style={{ marginTop: 16 }}>
            {React.createElement(DS.Banner, { tone: "info", title: `${plural(bands.unknown, "term", "terms")} could not be counted` },
              "Each of these says why in its own row. They are not terms with no content: they are terms nothing was able "
              + "to look for, and treating them as empty would be a claim about pages nobody has read.")}
          </div>
        )}
      </>
    );
  }

  window.MlCoverageMap = { CoverageMap };
})();
